import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, Dialog, FormControl, Grid, MenuItem, Select, SelectChangeEvent, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import MenuBookIcon from "@mui/icons-material/MenuBook";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY, NO_DATA_TO_DISPLAY_URL, VIMEO_PLAYER_URL } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import { FormData, useProgramForm } from './hooks/useVideosForm';
import { uploadFile } from '../../api/services/file';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { createVideo, deleteVideo, getVideos, getVideosByProgram, updateVideo } from '../../api/services/video';
import { Video } from '../../api/interfaces/Video';
import { Subject } from '../../api/interfaces/Subject';
import { getSubjects } from '../../api/services/subjects';
import SkeletonSubjectLists from '../../components/skeleton/SkeletonSubjectLists/SkeletonSubjectLists';
import RearrangeVideosDrawerContent from './partials/RearrangeVideosDrawerContent';
import VideoListItem from './partials/VideoListItem';
import CreateVideoDrawerContent from './partials/CreateVideoDrawerContent';
import EditVideoDrawerContent from './partials/EditVideoDrawerContent';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import { useDebounce } from 'use-debounce';
import ControlSettingsDrawerContentContent from './partials/ControlSettingsDrawerContent';

const s3BucketName = 'videos';

const VideosPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = {
        subjectId: '',
        programId: '',
        isTrial: false,
        isVisible: false,
        title: '',
        description: '',
        thumbnail: '',
        videoUrl: '',
        videoUrl1: '',
        videoUrl2: '',
        duration: ''
    };

    // Initial touch fields for error validation
    const initialTouchFields = {
        subjectId: false,
        programId: false,
        isTrial: false,
        isVisible: false,
        title: false,
        description: false,
        thumbnail: false,
        videoUrl: false,
        videoUrl1: false,
        videoUrl2: false,
        duration: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useProgramForm({
        initialFormData,
    });

    const [listViewType, setListViewType] = useState<'subjects' | 'videos'>('subjects');
    const [isSubjectsListViewLoading, setIsSubjectsListViewLoading] = useState<boolean>(false);

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [totalSubjectsPerProgram, setTotalSubjectsPerProgram] = useState<number>(0);
    const [videos, setVideos] = useState<Video[]>([]);
    const [isSubjectsLoading, setIsSubjectsLoading] = useState<boolean>(false);
    const [isVideosLoading, setIsVideosLoading] = useState<boolean>(false);

    // Search videos with debounce
    const [searchVideos, setSearchVideos] = useState<string>('');
    const [debouncedSearchVideos] = useDebounce(searchVideos, 500);
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    // Drawable states for create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected video id for edit state
    const [selectedVideoId, setSelectedVideoId] = useState<string>('');
    const [selectedVideoTitle, setSelectedVideoTitle] = useState<string>('');

    const [selectedSubjectId, setSelectedSubjectId] = useState<string>('');

    // Set the selected program id for edit state
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');
    const [selectedProgramTitle, setSelectedProgramTitle] = useState<string>('');
    const [selectedProgramOverallVideo, setSelectedProgramOverallVideo] = useState<number>(0);

    // Accordion state 
    const [expanded, setExpanded] = useState<string | false>(false);

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Video thumbnail
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchSubjects = useCallback(async (programId: string, keyword?: string) => {
        try {
            const subjectsResponse = await getSubjects({ programId, keyword });
            setSubjects(subjectsResponse.data.data);
            setTotalSubjectsPerProgram(subjectsResponse.data.total);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchAccordionVideos = useCallback(async (subjectId: string, programId: string) => {
        try {
            const videosResponse = await getVideos({ programId: programId, subjectId });
            setVideos(videosResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSearchResults = useCallback(async (programId: string, searchTerm: string) => {
        try {
            setIsVideosLoading(true);
            await fetchVideosByProgramId(programId, searchTerm);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsVideosLoading(false);
        } finally {
            setIsVideosLoading(false);
        }
    }, [debouncedSearchVideos]);

    // Overall video list view
    const fetchVideosByProgramId = useCallback(async (programId: string, keyword?: string) => {
        try {
            const videosByProgramResponse = await getVideosByProgram(programId, { keyword });
            setVideos(videosByProgramResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Initial fetch
    const fetchProgramSubjects = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            if (programResponse.data.length > 0) {
                const firstProgram = programResponse.data[0];
                setSelectedProgramId(firstProgram._id);
                setSelectedProgramTitle(firstProgram.name);
                setSelectedProgramOverallVideo(firstProgram.totalVideos);

                // Retreive the first record right ahead
                await fetchSubjects(firstProgram._id);
            }
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [fetchSubjects]);

    const handleAccordionChange = (panel: string, programId: string) => async (_: React.SyntheticEvent, isExpanded: boolean) => {
        if (isVideosLoading) return;
        setExpanded(isExpanded ? panel : false);
        if (isExpanded) {
            try {
                setIsVideosLoading(true);
                setVideos([]);
                await fetchAccordionVideos(panel, programId);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsVideosLoading(false);
                setVideos([]);
            } finally {
                setIsVideosLoading(false);
            }
        }
    };

    const handleVideosCallback = async (programId: string) => {
        try {
            // TBA IF VIDEO LIST VIEW
            // if (listViewType)
            // setSearchVideos('');
            // setIsSubjectsLoading(true);
            // await fetchVideos(programId);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSubjectsLoading(false);
        } finally {
            setIsSubjectsLoading(false);
        }
    }

    const handleChangeListView = async (type: 'subjects' | 'videos', count: number) => {
        if (listViewType !== type) {
            setListViewType(type);
            try {
                if (type === 'subjects') {
                    setIsSubjectsListViewLoading(true);
                    await fetchSubjects(selectedProgramId);
                    setIsSubjectsListViewLoading(false);
                } else if (type === 'videos') {
                    setIsVideosLoading(true);
                    setVideos([]);
                    await fetchVideosByProgramId(selectedProgramId);
                    setIsVideosLoading(false);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubjectsListViewLoading(false);
                setIsVideosLoading(false);
            } finally {
                setIsSubjectsListViewLoading(false);
                setIsVideosLoading(false);
            }
        }
    }

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'create' | 'edit' | string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add New Video' : state === 'edit' ? 'Edit Video' : state === 'rearrange' ? 'Rearrange Videos' : state === 'controls' ? 'Video Player Control Settings' : '');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let thumbnail = formData.thumbnail;
            if (selectedImage) {
                const file = selectedImage;
                const uploadResponse = await uploadFile(s3BucketName, file);
                thumbnail = uploadResponse.data.location;

                setRawFormData((prev) => ({ ...prev, thumbnail }));
                setFormData((prev) => ({ ...prev, thumbnail }));
            }

            let videoUrl2 = formData.videoUrl2;
            if (videoUrl2) {
                videoUrl2 = `${VIMEO_PLAYER_URL}/${videoUrl2}`;
            }

            const payload = {
                ...formData,
                thumbnail,
                videoUrl2
            };

            const createResponse = await createVideo(payload);

            // Check if the created video has the filter in the main list item (subjects/videos)
            if (formData.programId === selectedProgramId) {
                setSelectedProgramOverallVideo((selectedProgramOverallVideo + 1))
                if (listViewType === 'subjects') {
                    setIsSubjectsListViewLoading(true);
                    await fetchSubjects(selectedProgramId);
                    setIsSubjectsListViewLoading(false);
                    setExpanded(false);
                } else if (listViewType === 'videos') {
                    setVideos((prev) => [createResponse.data, ...prev]);
                }
            }
            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setCurrentDrawableState('');
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData),
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleEdit = (selectedVideo: Video) => {
        const { _id, title, description, programId, subjectId, thumbnail, videoUrl, videoUrl1, videoUrl2, duration, isTrial, isVisible } = selectedVideo
        setSelectedSubjectId(subjectId);
        setSelectedVideoId(_id);
        setSelectedVideoTitle(title);

        const videoData: FormData = {
            subjectId,
            programId,
            isTrial,
            isVisible,
            title,
            description,
            thumbnail,
            videoUrl,
            videoUrl1: videoUrl1 ?? '',
            videoUrl2: videoUrl2 ?? '',
            duration
        };
        setOriginalFormData(videoData as FormData);
        resetForm(videoData);
        toggleDrawer(true, 'edit')();
    };

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let thumbnail = formData.thumbnail;
            if (selectedImage) {
                const file = selectedImage;
                const uploadResponse = await uploadFile(s3BucketName, file);
                thumbnail = uploadResponse.data.location;

                setRawFormData((prev) => ({ ...prev, thumbnail }));
                setFormData((prev) => ({ ...prev, thumbnail }));
            }

            let videoUrl2 = formData.videoUrl2?.trim();

            if (videoUrl2) {
                // Only add prefix if it's not already a full Vimeo URL
                if (!videoUrl2.startsWith(VIMEO_PLAYER_URL)) {
                    videoUrl2 = `${VIMEO_PLAYER_URL}/${videoUrl2}`;
                }
            }

            const payload = {
                ...formData,
                thumbnail,
                videoUrl2
            };
            const updateResponse = await updateVideo(selectedVideoId, payload);
            toggleDrawer(false, '')();
            if (listViewType === "subjects") {
                if (
                    formData.programId !== selectedProgramId ||
                    formData.subjectId !== selectedSubjectId
                ) {
                    setIsSubjectsListViewLoading(true);
                    await fetchSubjects(selectedProgramId);
                    setIsSubjectsListViewLoading(false);
                    setExpanded(false);
                } else {
                    setVideos((prev) =>
                        prev.map((video) =>
                            video._id === updateResponse.data._id ? updateResponse.data : video
                        )
                    );
                }
            } else if (listViewType === "videos") {
                setVideos((prev) =>
                    prev.map((video) =>
                        video._id === updateResponse.data._id ? updateResponse.data : video
                    )
                );
            }
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDelete = (selectedVideo: Video) => {
        setSelectedVideoId(selectedVideo._id);
        setSelectedVideoTitle(selectedVideo.title);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteVideo(selectedVideoId);
            setVideos(prev => prev.filter(ref => ref._id !== selectedVideoId));
            setSelectedProgramOverallVideo((selectedProgramOverallVideo - 1))
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedVideoId('');
            setSelectedVideoTitle('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            setRawFormData((prev) => ({
                ...prev,
                thumbnail: 'selected',
            }));
        }
    };

    const handleDrawerClose = () => {
        toggleDrawer(false, currentDrawableState)()
        setRawFormData(initialFormData);
        setFormData(initialFormData);
        setTouchedFields(initialTouchFields);
        setCurrentDrawableState('');
        setIsSubmitting(false);

        // Clear file input and image
        setSelectedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFilterByProgramChange = async (event: SelectChangeEvent<string>) => {
        try {
            setSearchVideos('');
            setIsSubjectsLoading(true);
            setListViewType('subjects');
            setSubjects([]);
            setVideos([]);
            setExpanded(false);
            setSelectedProgramId(event.target.value);
            const selectedProgram = programs.find(program => program._id === event.target.value);
            setSelectedProgramTitle(selectedProgram?.name || '');
            setSelectedProgramOverallVideo(selectedProgram?.totalVideos || 0);

            await fetchSubjects(event.target.value);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSubjectsLoading(false);
        } finally {
            setIsSubjectsLoading(false);
        }
    };

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            setIsSubjectsLoading(true);
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }
                setCurrentAdminName(firstName);
                await fetchProgramSubjects();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubjectsLoading(false);
            } finally {
                setIsSubjectsLoading(false);
            }
        };
        run();
    }, [fetchProgramSubjects]);

    // Debouncing mechanism for search reference
    useEffect(() => {
        const search = async () => {
            if (!selectedProgramId) return;

            const trimmed = debouncedSearchVideos.trim();

            if (trimmed !== '') {
                setHasSearched(true);
                setListViewType('videos');
                setVideos([]);
                setIsVideosLoading(true);
                try {
                    await fetchSearchResults(selectedProgramId, trimmed);
                } finally {
                    setIsVideosLoading(false);
                }
            } else if (hasSearched) {
                setListViewType('videos');
                setVideos([]);
                setIsVideosLoading(true);
                try {
                    await fetchVideosByProgramId(selectedProgramId);
                } finally {
                    setIsVideosLoading(false);
                }
            }
        };

        search();
    }, [debouncedSearchVideos, selectedProgramId, hasSearched]);


    const NoDataToDisplay = () => {
        return (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#818181'
                }}
            >
                <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
            </Box>
        )
    }
    return (
        <main className="main-content">
            <Sidebar />

            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Videos' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: '2'
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'create')}
                            disabled={isSubjectsLoading || isVideosLoading}
                        >
                            New Video
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<DragIndicatorIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'rearrange')}
                            disabled={isSubjectsLoading || isVideosLoading}
                        >
                            Rearrange Videos
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<VideoSettingsIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'controls')}
                            disabled={isSubjectsLoading || isVideosLoading}
                        >
                            Player Controls
                        </Button>

                        <PinnacleSearchField
                            id="searchVideos"
                            label="Search videos here..."
                            value={searchVideos}
                            onChange={setSearchVideos}
                            isLoading={isVideosLoading}
                        />
                    </Box>
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                            <Typography variant="body1" fontWeight={600}>
                                List of Videos {selectedProgramTitle && `under ${selectedProgramTitle}`}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#818181' }}>
                                    view by
                                </Typography>

                                {isSubjectsLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        width={250}
                                        height={40}
                                        sx={{ borderRadius: 1 }}
                                    />
                                ) : (
                                    <FormControl>
                                        <Select
                                            value={selectedProgramId}
                                            onChange={handleFilterByProgramChange}
                                            defaultValue={selectedProgramId}
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Select Program' }}
                                            sx={{
                                                minWidth: 250,
                                                maxWidth: 250,
                                                color: '#818181',
                                                '.MuiSelect-icon': {
                                                    color: '#818181',
                                                },
                                            }}
                                            MenuProps={{
                                                disableScrollLock: true,
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            }}
                                        >
                                            {programs.map((program) => (
                                                <MenuItem key={program._id} value={program._id}>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            maxWidth: '200px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {program.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        </Box>
                        <Grid container rowSpacing={1} columnSpacing={1}>
                            {isSubjectsLoading ? (
                                <SkeletonSubjectLists />
                            ) : (
                                <Grid size={12}>
                                    {/* Green line / parent heading */}
                                    <Box
                                        sx={{
                                            backgroundColor: "#028A4F",
                                            color: "white",
                                            p: 1.5,
                                            px: 3,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderRadius: "6px 6px 0 0",
                                        }}
                                    >
                                        {/* Left side: Program Title */}
                                        <Typography variant="body2" fontWeight={600}>
                                            {selectedProgramTitle}
                                        </Typography>

                                        {/* Right side: Subjects | Videos */}
                                        {!searchVideos && (
                                            <Typography variant="subtitle2">
                                                Subjects:{" "}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "inherit",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => handleChangeListView('subjects', subjects.length)}
                                                >
                                                    {totalSubjectsPerProgram}
                                                </Typography>
                                                &nbsp; | &nbsp; Videos:{" "}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "inherit",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => handleChangeListView('videos', selectedProgramOverallVideo)}
                                                >
                                                    {selectedProgramOverallVideo}
                                                </Typography>
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            border: "1px solid #ddd",
                                            borderTop: "none",
                                            borderRadius: "0 0 6px 6px",
                                            backgroundColor: "#fafafa",
                                            px: 2,
                                            py: 1.5,
                                        }}
                                    >
                                        {listViewType === 'subjects' ? (
                                            isSubjectsLoading || isSubjectsListViewLoading ? (
                                                Array.from({ length: 3 }).map((_, idx) => (
                                                    <Skeleton
                                                        key={idx}
                                                        variant="rectangular"
                                                        height={50}
                                                        sx={{ borderRadius: 1, mb: 1 }}
                                                    />
                                                ))
                                            ) : subjects.length === 0 ? (
                                                <NoDataToDisplay />
                                            ) : (
                                                subjects.map((subject: Subject) => (
                                                    <>
                                                        <Accordion
                                                            key={subject._id}
                                                            expanded={expanded === subject._id}
                                                            onChange={handleAccordionChange(subject._id, subject.programId)}
                                                            disableGutters
                                                            elevation={0}
                                                            square
                                                            sx={{
                                                                borderBottom: "1px solid #eee",
                                                                "&:before": { display: "none" },
                                                                backgroundColor: "transparent",
                                                                boxShadow: "none",
                                                            }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={
                                                                    <AddIcon
                                                                        sx={{
                                                                            fontSize: 18,
                                                                            transition: "transform 0.2s",
                                                                        }}
                                                                    />
                                                                }
                                                                sx={{
                                                                    px: 1.5,
                                                                    py: 0.75,
                                                                    minHeight: "unset",
                                                                    "& .MuiAccordionSummary-content": {
                                                                        margin: 0,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "space-between",
                                                                    },
                                                                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                                                                        transform: "rotate(45deg)",
                                                                    },
                                                                }}
                                                            >
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                                                    <MenuBookIcon fontSize="small" sx={{ color: '#A8A8A8' }} />
                                                                    <Box>
                                                                        <Typography fontWeight={600} variant="body2">
                                                                            {subject.name}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Videos: {subject.totalVideos}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                {isVideosLoading ? (
                                                                    Array.from({ length: 5 }).map((_, idx) => (
                                                                        <Skeleton
                                                                            key={idx}
                                                                            variant="rectangular"
                                                                            height={50}
                                                                            sx={{ borderRadius: 1, mb: 1 }}
                                                                        />
                                                                    ))
                                                                ) : videos && videos.length > 0 ? (
                                                                    <Box
                                                                        sx={{
                                                                            maxHeight: 600,
                                                                            overflowY: "auto",
                                                                            pr: 1,
                                                                            "&::-webkit-scrollbar": {
                                                                                width: "6px",
                                                                            },
                                                                            "&::-webkit-scrollbar-thumb": {
                                                                                backgroundColor: "#ccc",
                                                                                borderRadius: "4px",
                                                                            },
                                                                        }}
                                                                    >
                                                                        {videos.map((video) => (
                                                                            <VideoListItem
                                                                                video={video}
                                                                                onEdit={() => handleEdit(video)}
                                                                                onDelete={() => handleDelete(video)}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    <NoDataToDisplay />
                                                                )}

                                                            </AccordionDetails>
                                                        </Accordion>
                                                    </>
                                                ))
                                            )
                                        ) : listViewType === 'videos' ? (
                                            isVideosLoading ? (
                                                Array.from({ length: 5 }).map((_, idx) => (
                                                    <Skeleton
                                                        key={idx}
                                                        variant="rectangular"
                                                        height={50}
                                                        sx={{ borderRadius: 1, mb: 1 }}
                                                    />
                                                ))
                                            ) : videos && videos.length > 0 ? (
                                                <Box
                                                    sx={{
                                                        maxHeight: 600,
                                                        overflowY: "auto",
                                                        pr: 1,
                                                        "&::-webkit-scrollbar": {
                                                            width: "6px",
                                                        },
                                                        "&::-webkit-scrollbar-thumb": {
                                                            backgroundColor: "#ccc",
                                                            borderRadius: "4px",
                                                        },
                                                    }}
                                                >
                                                    {videos.map((video) => (
                                                        <VideoListItem
                                                            video={video}
                                                            onEdit={() => handleEdit(video)}
                                                            onDelete={() => handleDelete(video)}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <NoDataToDisplay />
                                            )
                                        ) : ''}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
                customWidthSize={(currentDrawableState === 'create' || currentDrawableState === 'edit') ? 700 : undefined}
            >

                {currentDrawableState === 'rearrange' && (
                    <RearrangeVideosDrawerContent
                        programs={programs}
                        firstProgramId={selectedProgramId}
                        firstProgramTitle={selectedProgramTitle}
                        closeToggle={toggleDrawer(false, '')}
                        onVideosCallback={handleVideosCallback}
                    />
                )}

                {currentDrawableState === 'create' && (
                    <CreateVideoDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditVideoDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                        isFormModified={isFormModified}
                    />
                )}

                {currentDrawableState === 'controls' && (
                    <ControlSettingsDrawerContentContent
                        closeToggle={toggleDrawer(false, '')}
                    />
                )}
            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Program"
                    itemToDelete={selectedVideoTitle}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default VideosPage;
